// src/components/invite/InviteRouter.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import { InvitationType } from '../../types/Invitation';
import { EventType }      from '../../types/Event';
import { TemplateType }   from '../../types/Template';
import VideoThemePlayer   from '../player/VideoThemePlayer';
import BirthdayLoadingDisplay from '../player/BirthdayLoadingDisplay';   // ← NEW

type ViewState = 'intro' | 'playing' | 'completed';

/* ─── Utility: 24‑hour ➜ 12‑hour ─────────────────────────────────── */
function to12Hour(time24?: string | null): string {
  if (!time24 || typeof time24 !== 'string') return '';

  // If the string already has AM/PM just normalise spacing and return
  if (/\b(am|pm)\b/i.test(time24)) {
    return time24.replace(/\s+/g, ' ').trim();
  }

  const m = time24.trim().match(/^(\d{1,2}):?(\d{2})?$/);
  if (!m) return time24;                  // Unrecognised → leave unchanged

  let   hrs  = parseInt(m[1], 10);
  const mins = parseInt(m[2] ?? '0', 10);

  if (hrs > 23 || mins > 59) return time24;   // Fail fast on nonsense

  const period = hrs >= 12 ? 'PM' : 'AM';
  hrs = hrs % 12 || 12;                       // 0→12, 13→1, etc.

  return `${hrs}:${mins.toString().padStart(2, '0')} ${period}`;
}

export default function InviteRouter() {
  /* ─── Routing & query params ───────────────────────────────────── */
  const navigate   = useNavigate();
  const { code }   = useParams<{ code: string }>();   // (kept for future)
  const location   = useLocation();
  const inviteId   = new URLSearchParams(location.search).get('id') || '';

  /* ─── Local state ──────────────────────────────────────────────── */
  const [invitation, setInvitation] = useState<InvitationType | null>(null);
  const [eventDoc,   setEventDoc]   = useState<EventType   | null>(null);
  const [template,   setTemplate]   = useState<TemplateType| null>(null);
  const [viewState,  setViewState]  = useState<ViewState>('playing');
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  /* ─── Firestore fetch ──────────────────────────────────────────── */
  useEffect(() => {
    const loadData = async () => {
      if (!inviteId) { setError('No invitation ID provided'); setLoading(false); return; }

      try {
        /* 1️⃣ Track view */
        await httpsCallable(functions, 'trackInvitationView')({ invitationId: inviteId });

        /* 2️⃣ Invitation */
        const invSnap = await getDoc(doc(db, 'invitations', inviteId));
        if (!invSnap.exists()) { setError('Invitation not found'); setLoading(false); return; }
        const invData = { id: invSnap.id, ...(invSnap.data() as any) } as InvitationType;
        setInvitation(invData);

        /* Persist "playing" once on first open (non‑anonymous only) */
        if (invData.recipientName !== 'Anonymous' &&
            (invData as any).viewState !== 'completed') {
          await updateDoc(doc(db, 'invitations', invData.id!), { viewState: 'playing' });
        }
        setViewState('playing');

        /* 3️⃣ Event */
        const evtSnap = await getDoc(doc(db, 'events', invData.eventId));
        if (!evtSnap.exists()) { setError('Event not found'); setLoading(false); return; }
        const evtData = { id: evtSnap.id, ...(evtSnap.data() as any) } as EventType;
        setEventDoc(evtData);

        /* 4️⃣ Template */
        if (!evtData.templateId) { setError('No template associated with this event'); setLoading(false); return; }
        const tplSnap = await getDoc(doc(db, 'templates', evtData.templateId));
        if (!tplSnap.exists()) { setError('Template not found'); setLoading(false); return; }
        const tplData = { id: tplSnap.id, ...(tplSnap.data() as any) } as TemplateType;
        setTemplate(tplData);

        setLoading(false);
      } catch (err) {
        console.error('Error loading invitation data:', err);
        setError('Failed to load invitation details');
        setLoading(false);
      }
    };

    loadData();
  }, [inviteId]);

  /* ─── Helpers ──────────────────────────────────────────────────── */
  const finishInvitation = async (
    status: 'accepted' | 'declined',
    peopleCount?: number
  ) => {
    if (!invitation) return;

    await httpsCallable(functions, 'trackInvitationResponse')({
      invitationId: invitation.id,
      response:     status,
      peopleCount:  peopleCount || 1
    });

    if (invitation.recipientName !== 'Anonymous') {
      await updateDoc(doc(db, 'invitations', invitation.id!), {
        viewState:   'completed',
        status,
        respondedAt: new Date(),
        peopleCount: peopleCount || 1
      });
    }
  };

  /* ─── Guards ───────────────────────────────────────────────────── */
  if (loading) return <BirthdayLoadingDisplay />;   // ← show loader only here
  if (error)   return <div className="error">{error}</div>;
  if (!invitation || !eventDoc || !template)
    return <div className="error">Unable to load invitation details</div>;

  /* ─── Video page build ─────────────────────────────────────────── */
  if (viewState === 'playing' || viewState === 'completed') {
    const recipientFirstName =
      invitation.recipientName === 'Anonymous'
        ? ''
        : (invitation.recipientName?.trim().split(' ')[0] ?? '');

    const hasResponded = invitation.recipientName !== 'Anonymous' &&
                         ['accepted', 'declined'].includes((invitation as any).status);

    /* Raw times may live either on the event doc or inside customData */
    const rawStart = (eventDoc.eventTime ?? eventDoc.customData?.sTime) as string | undefined;
    const rawEnd   = (eventDoc.endTime  ?? eventDoc.customData?.eTime) as string | undefined;

    const mergedData = {
      /* ↓ customData FIRST so explicit keys below override it ↓ */
      ...eventDoc.customData,

      /* Recipient */
      recipientName: recipientFirstName,

      /* Event specifics */
      eventTitle: eventDoc.title,
      eventDate:  eventDoc.eventDate?.toDate?.()
                    ? eventDoc.eventDate.toDate().toLocaleDateString()
                    : '',
      sTime:      to12Hour(rawStart),
      eTime:      to12Hour(rawEnd),
      address:    eventDoc.location ?? '',

      /* IDs */
      invitationId: invitation.id,

      /* Media */
      mainImageUrl:  eventDoc.customData?.mainImageUrl,
      eventVideoUrl: eventDoc.customData?.eventVideoUrl,

      /* Template */
      animationJson: template.animationJson,

      /* Callbacks */
      onAccept:  (pc?: number) => finishInvitation('accepted', pc),
      onDecline: ()            => finishInvitation('declined'),

      /* Existing response (if any) */
      responseStatus: hasResponded ? (invitation as any).status : null,
      peopleCount:    (invitation as any).peopleCount || null,

      /* Misc */
      uniqueCode: invitation.uniqueCode,
      _debug_id:  Math.random().toString(36).slice(2, 9)
    };

    console.log('Passing data to VideoThemePlayer:', mergedData);

    return (
      <VideoThemePlayer
        template={template}
        data={mergedData}
        mode="live"
      />
    );
  }

  /* Should never reach here */
  return <div className="error">Invalid state</div>;
}
